import React, { useState, useContext, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Alert, IconButton, InputAdornment, useTheme } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ConstantsContext } from "utils/const";
import { FormControl } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import OTPInput from "components/OTPInput";
import { generateUniqueId } from "utils";
import { validateWhatsappNumber } from "utils/phoneValidation";
import FlexBetween from "components/FlexBetween";
import AuthLayout, { authInputSx, authFieldLabelSx, authButtonSx } from "components/AuthLayout";
import { useTranslation } from "react-i18next";

const baseUrl = process.env.REACT_APP_BASE_URL + "/public";

// Steps: "request-otp" -> "verify-otp" -> "set-new-password" -> "success"
export default function ForgotPassword() {
  const theme = useTheme();
  const { t } = useTranslation();
  const constants = useContext(ConstantsContext);
  const countryRules = constants.countryRules;

  const navigate = useNavigate();
  const [step, setStep] = useState("request-otp");
  const [errorMsg, setErrorMsg] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ref, setRef] = useState("");
  const [otpJobId, setOtpJobId] = useState("");
  const [otpDeliveryStatus, setOtpDeliveryStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const otpLength = 6;
  const [otp, setOtp] = useState(new Array(otpLength).fill(""));
  const [otpVerifyFailed, setOtpVerifyFailed] = useState(false);

  // New password fields
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  const uniqueCountryRules = countryRules.filter(
    (country, index, self) =>
      index === self.findIndex((c) => c.phone_code === country.phone_code)
  );

  const autocompleteOptions = uniqueCountryRules.map((country) => ({
    label: `(${country.phone_code}) ${country.code} - ${country.name}`,
    value: country.phone_code,
  }));

  useEffect(() => {
    const laosRule = countryRules.find((country) => country.code === "LA");
    if (laosRule) {
      setCountryCode(laosRule.phone_code);
    }
  }, [countryRules]);

  const startResendTimer = () => {
    setTimerSeconds(60);
    setIsResendDisabled(true);
  };

  useEffect(() => {
    let intervalId;
    if (step === "verify-otp" && timerSeconds > 0) {
      intervalId = setInterval(() => {
        setTimerSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setIsResendDisabled(false);
      clearInterval(intervalId);
    }
    return () => clearInterval(intervalId);
  }, [step, timerSeconds]);

  const requestOtp = async (phone, refId) => {
    const response = await axios.post(
      baseUrl + "/otp-request",
      {
        phone,
        ref: refId,
        otpType: "reset-password",
      },
      { withCredentials: true }
    );
    setOtpJobId(response?.data?.jobId || "");
    setOtpDeliveryStatus("queued");
    return response;
  };

  useEffect(() => {
    if (step !== "verify-otp" || !otpJobId || !phoneNumber || !ref) {
      return;
    }

    let intervalId;
    let stopped = false;

    const pollOtpDeliveryStatus = async () => {
      try {
        const response = await axios.post(
          baseUrl + "/otp-delivery-status",
          {
            jobId: otpJobId,
            phone: phoneNumber,
            ref,
            otpType: "reset-password",
          },
          { withCredentials: true }
        );

        const status = response?.data?.deliveryStatus;
        setOtpDeliveryStatus(status || "");

        if (status === "failed" || status === "cancelled") {
          setOtpVerifyFailed(true);
          setErrorMsg(
            response?.data?.lastErrorMessage ||
              "OTP delivery failed. Please tap Resend OTP."
          );
          setIsResendDisabled(false);
          setTimerSeconds(0);
          clearInterval(intervalId);
        }

        if (status === "sent") {
          clearInterval(intervalId);
        }
      } catch (error) {
        if (!stopped) {
          console.log("OTP delivery status check failed:", error?.message);
        }
      }
    };

    pollOtpDeliveryStatus();
    intervalId = setInterval(pollOtpDeliveryStatus, 3000);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [step, otpJobId, phoneNumber, ref]);

  const handleOTPRequestSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    setErrorMsg("");

    const validation = validateWhatsappNumber(
      countryRules,
      countryCode,
      whatsappNumber
    );
    if (!validation.valid) {
      setErrorMsg(validation.message);
      return;
    }

    setIsLoading(true);
    const phone = validation.fullNumber;
    setPhoneNumber(phone);
    const genId = generateUniqueId(10);
    setRef(genId);

    try {
      await requestOtp(phone, genId);
      setStep("verify-otp");
      startResendTimer();
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Unable to send OTP. Please try again later.";
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPComplete = async (otpValue) => {
    setIsLoading(true);
    setOtpVerifyFailed(false);
    setErrorMsg("");

    try {
      const res = await axios.post(
        baseUrl + "/otp-verify",
        {
          phone: phoneNumber,
          otp: otpValue,
          ref,
          otpType: "reset-password",
        },
        { withCredentials: true }
      );

      if (res && res.status === 200) {
        // OTP verified — backend has set a short-lived reset-password cookie.
        setStep("set-new-password");
      }
    } catch (error) {
      setOtpVerifyFailed(true);
      setErrorMsg(
        error.response?.data?.message ||
          "OTP verification failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTPButton = async () => {
    setOtp(new Array(otpLength).fill(""));
    setOtpVerifyFailed(false);
    setErrorMsg("");
    setOtpDeliveryStatus("");
    setOtpJobId("");
    setIsLoading(true);

    const phone = phoneNumber;
    const genId = generateUniqueId(10);
    setRef(genId);

    try {
      await requestOtp(phone, genId);
      startResendTimer();
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message ||
          "OTP request failed, please try again later"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const validateNewPassword = () => {
    const errors = {};
    if (!newPassword || newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    } else if (!/(?=.*[a-z])/.test(newPassword)) {
      errors.newPassword = "Password must contain at least one lowercase letter";
    } else if (!/(?=.*[A-Z])/.test(newPassword)) {
      errors.newPassword = "Password must contain at least one uppercase letter";
    } else if (!/(?=.*\d)/.test(newPassword)) {
      errors.newPassword = "Password must contain at least one number";
    }
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPasswordSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (!validateNewPassword()) return;

    setIsLoading(true);
    setErrorMsg("");

    try {
      const res = await axios.post(
        baseUrl + "/reset-password",
        { password: newPassword, confirmPassword },
        { withCredentials: true }
      );
      if (res.status === 200) {
        setStep("success");
        setTimeout(() => navigate("/login"), 4000);
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Unable to reset password. Please verify OTP again.";
      setErrorMsg(msg);
      // If session expired, send back to OTP request step
      if (error.response?.status === 401) {
        setTimeout(() => setStep("request-otp"), 2500);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputSx = authInputSx(theme);
  const fieldLabelSx = authFieldLabelSx(theme);

  const features = [
    t("clientLogin.feature1"),
    t("clientLogin.feature2"),
    t("clientLogin.feature3"),
  ];

  const titles = {
    "request-otp": ["Reset your password", "Enter your WhatsApp number to receive a verification code"],
    "verify-otp": ["Enter verification code", `We sent a code to your WhatsApp number ${countryCode + whatsappNumber}`],
    "set-new-password": ["Set a new password", "Choose a strong password (8+ characters with uppercase, lowercase, and a number)."],
    success: ["Password reset successfully", "Redirecting you to login..."],
  };
  const [title, subtitle] = titles[step];

  return (
    <AuthLayout tagline={t("clientLogin.tagline")} features={features}>
      <Box sx={{ mb: "34px" }}>
        <Typography sx={{ fontFamily: "'Space Grotesk', 'Noto Sans Lao', sans-serif", fontSize: 30, fontWeight: 700, color: "#1b1728", lineHeight: 1.2, mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#9b8ab8", fontWeight: 500 }}>
          {subtitle}
        </Typography>
      </Box>

      {step === "request-otp" && (
        <Box component="form" noValidate onSubmit={handleOTPRequestSubmit}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={fieldLabelSx}>Country Code</Typography>
            <FormControl fullWidth required>
              <Autocomplete
                id="country-code"
                name="countryCode"
                options={autocompleteOptions}
                disabled={isLoading}
                getOptionLabel={(option) => option.label}
                value={
                  autocompleteOptions.find(
                    (option) => option.value === countryCode
                  ) || null
                }
                onChange={(event, newValue) => {
                  setCountryCode(newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select country code" sx={inputSx} />
                )}
              />
            </FormControl>
          </Box>
          <Box sx={{ mb: "14px" }}>
            <Typography component="label" htmlFor="whatsappNumber" sx={fieldLabelSx}>
              WhatsApp Number
            </Typography>
            <TextField
              required
              fullWidth
              id="whatsappNumber"
              name="whatsappNumber"
              placeholder="Enter your WhatsApp number"
              autoComplete="whatsapp"
              autoFocus
              disabled={isLoading}
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsAppIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
          )}

          <Button type="submit" disabled={isLoading} fullWidth variant="contained" color="secondary" sx={authButtonSx(theme)}>
            {isLoading ? "Sending..." : "Send OTP"}
          </Button>
        </Box>
      )}

      {step === "verify-otp" && (
        <Box>
          <OTPInput
            otp={otp}
            setOtp={setOtp}
            length={otpLength}
            onComplete={handleOTPComplete}
            fontSize="14px"
            boxWidth="18px"
          />
          {otpVerifyFailed && errorMsg && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>
          )}
          {!otpVerifyFailed && otpDeliveryStatus && otpDeliveryStatus !== "sent" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              We're sending OTP to {whatsappNumber.slice(0, 3)}{"X".repeat(Math.max(0, whatsappNumber.length - 6))}{whatsappNumber.slice(-3)}
            </Alert>
          )}
          <FlexBetween sx={{ mt: 2, mb: 2 }}>
            <Typography sx={{ fontSize: 13, color: "#9b8ab8" }}>Reference no. {ref}</Typography>
          </FlexBetween>
          <Button
            disabled={isLoading || isResendDisabled}
            fullWidth
            variant="contained"
            color="secondary"
            sx={authButtonSx(theme)}
            onClick={handleResendOTPButton}
          >
            {isResendDisabled ? `Resend OTP in ${timerSeconds}s` : "Resend OTP"}
          </Button>
        </Box>
      )}

      {step === "set-new-password" && (
        <Box component="form" noValidate onSubmit={handleResetPasswordSubmit}>
          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="newPassword" sx={fieldLabelSx}>
              New Password
            </Typography>
            <TextField
              required
              fullWidth
              id="newPassword"
              placeholder="Enter your new password"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={!!passwordErrors.newPassword}
              helperText={passwordErrors.newPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((p) => !p)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18, color: "#9d8bc4" }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18, color: "#9d8bc4" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          <Box sx={{ mb: "14px" }}>
            <Typography component="label" htmlFor="confirmPassword" sx={fieldLabelSx}>
              Confirm New Password
            </Typography>
            <TextField
              required
              fullWidth
              id="confirmPassword"
              placeholder="Confirm your new password"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Box>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
          )}

          <Button type="submit" disabled={isLoading} fullWidth variant="contained" color="secondary" sx={authButtonSx(theme)}>
            {isLoading ? "Resetting..." : "Reset Password"}
          </Button>
        </Box>
      )}

      {step === "success" && (
        <Box textAlign="center">
          <Button fullWidth variant="contained" color="secondary" onClick={() => navigate("/login")} sx={authButtonSx(theme)}>
            Go to login
          </Button>
        </Box>
      )}
    </AuthLayout>
  );
}
